import { IS_STAGING, BASIC_AUTH, practiceArea, STAGING_VISITS_ENDPOINT_URL } from '../shared/config';
import { LawftyVisitsOptions, Visit, visitDetailsTypes } from '../shared/types';
import { generateIdempotencyKey } from './utils';

const getGaClientIdFromCookie = () => {
  const row = document.cookie.split('; ').find((c) => c.startsWith('_ga='));
  return row ? decodeURIComponent(row.split('=')[1]) : null;
};

export const lawftyVisits = (apiURL: string, { website, experiment }: LawftyVisitsOptions = {}) => {
  // Set visit expiration to 8 hours
  const visitExpirationInSeconds = 3600 * 8;
  const isStaging = IS_STAGING || apiURL === STAGING_VISITS_ENDPOINT_URL;

  const detectOperatingSystem = (platformInfo: string) => {
    if (platformInfo.indexOf('mac') !== -1) {
      return 'Mac OS';
    } else if (platformInfo.indexOf('win') !== -1) {
      return 'Windows';
    } else if (platformInfo.indexOf('linux') !== -1) {
      return 'Linux';
    } else if (platformInfo.indexOf('ios') !== -1) {
      return 'iOS';
    } else if (platformInfo.indexOf('android') !== -1) {
      return 'Android';
    }
    return 'Unknown';
  };

  const detectBrowser = (userAgent: string) => {
    if (userAgent.indexOf('Chrome') !== -1) {
      return 'Chrome';
    } else if (userAgent.indexOf('Firefox') !== -1) {
      return 'Firefox';
    } else if (userAgent.indexOf('Safari') !== -1) {
      return 'Safari';
    } else if (userAgent.indexOf('Edge') !== -1) {
      return 'Edge';
    } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
      return 'Internet Explorer';
    }
    return 'Unknown';
  };

  const detectDeviceType = (userAgent: string) => {
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
      return 'Mobile';
    } else if (/Tablet|iPad/i.test(userAgent)) {
      return 'Tablet';
    } else if (/Windows Phone/i.test(userAgent)) {
      return 'Windows Phone';
    } else {
      return 'Desktop';
    }
  };

  const getVisitParameters = () => {
    const userAgent = navigator.userAgent;
    const referrer = document.referrer;
    const landingPage = document.URL;

    // Use platform.js to get detailed OS information
    const platformInfo = platform.parse(userAgent);
    const os = detectOperatingSystem(platformInfo.os.family.toLowerCase());

    // Determine the browser without external libraries
    const browser = detectBrowser(userAgent);
    const deviceType = detectDeviceType(userAgent);

    // Include the utm_source parameter in the search attribute
    let search = window.location.search;

    // Check if utm_source parameter is already present
    if (!search.includes('utm_source=')) {
      // If not present, add it to the search parameters
      search += search.length === 0 ? '?' : '&';
      search += 'utm_source='; // Empty string as the default source
    }

    const params = new URLSearchParams(window.location.search);
    const utm_location = params.get('utm_location') || null;

    const visitDetails: visitDetailsTypes = {
      user_agent: userAgent,
      referrer: referrer,
      landing_page: landingPage,
      os,
      browser,
      device_type: deviceType,
      search,
      utm_location,
    };

    if (website) {
      visitDetails.website = website;
    }

    if (experiment) {
      visitDetails.experiment = experiment;
    }

    // attach GA4 client id if present
    const gaClientId = getGaClientIdFromCookie();
    if (gaClientId) {
      visitDetails.ga_client_id = gaClientId; // backend stores alongside visit_token
    }

    if (document.defaultView != null) {
      visitDetails.height = window.screen.height;
      visitDetails.width = window.screen.width;
    }

    return visitDetails;
  };

  // This is where we can implement our business logic if needed
  // Right now, let's only consider the entire searchParams string
  const getVisitKey = () => {
    let key = window.location.search;
    if (key == '?r=0') {
      key = '';
    }
    return key;
  };

  // Get visit data (token + phone number) from local storage
  const getVisitFromStorage = () => {
    const visitKey = getVisitKey();
    let storedVisitKey = localStorage.getItem('visitKey');

    // no match for visitKey, return empty
    if (visitKey && storedVisitKey != visitKey) {
      console.log('Visit key mismatch:', visitKey, storedVisitKey);
      return {};
    }

    // Storage expired?
    const expiration = Number(localStorage.getItem('expiresAt'));
    if (expiration && Date.now() > expiration) {
      console.log('Visit data expired:', visitKey);
      return {};
    }

    // Visit was created for another experiment, so its variant doesn't apply
    const storedExperiment = localStorage.getItem('experiment') || '';
    if (storedExperiment !== (experiment || '')) {
      console.log('Visit experiment mismatch:', experiment, storedExperiment);
      return {};
    }

    // Return stored data only if the visit key matches and it hasn't expired
    return {
      phoneNumber: localStorage.getItem('phoneNumber'),
      visitToken: localStorage.getItem('visitToken'),
      variant: localStorage.getItem('variant'),
      campaignId: localStorage.getItem('campaignId'),
      sourceId: localStorage.getItem('sourceId'),
      visitKey,
      utm_location_name: localStorage.getItem('utm_location_name') || null, // ✅ ADDED
    };
  };

  // Store visit in local storage
  const storeVisit = (visit: Visit) => {
    localStorage.setItem('phoneNumber', visit.phoneNumber);
    localStorage.setItem('visitToken', visit.visitToken);
    localStorage.setItem('visitKey', visit.visitKey);
    localStorage.setItem('variant', visit.variant || 'control');
    localStorage.setItem('experiment', experiment || '');
    localStorage.setItem('expiresAt', String(Date.now() + 1000 * visitExpirationInSeconds));
    localStorage.setItem('campaignId', visit.campaignId || 'N/A');
    localStorage.setItem('sourceId', visit.sourceId || 'N/A');
    localStorage.setItem('utm_location_name', visit.utm_location_name || '');
    localStorage.setItem('landingPracticeArea', practiceArea || '');
  };

  const getVisitFromAPI = async (visitAttributes: visitDetailsTypes): Promise<Visit> => {
    const maxRetries = 3;
    let attempt = 0;
    let lastError: Error | null = null;

    // generate one idempotency key per user action (reused across retries)
    const idempotencyKey = generateIdempotencyKey();

    // helper to pause
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    // if create visits fails, retry up to maxRetries times
    while (attempt < maxRetries) {
      try {
        const headers = {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey,
        };
        if (isStaging) {
          headers['Authorization'] = BASIC_AUTH;
        }

        const response = await fetch(apiURL, {
          method: 'POST',
          headers: {
            ...headers,
          },
          mode: 'cors',
          body: JSON.stringify(visitAttributes),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        return {
          phoneNumber: data['phone_number'],
          visitToken: data['visit_token'],
          variant: data['variant'],
          visitKey: getVisitKey(),
          campaignId: data['campaign_id'],
          sourceId: data['source_id'],
          utm_location_name: data['utm_location_name'] || null,
        };
      } catch (error) {
        lastError = error;
        attempt++;

        if (attempt >= maxRetries) {
          console.error(`All ${maxRetries} attempts failed.`);
          throw new Error(`getVisitFromAPI failed after ${maxRetries} attempts: ${lastError.message}`);
        }

        // wait 200ms before retrying
        await sleep(200);
      }
    }
  };

  // Main entry point
  const getVisitData = async () => {
    let visit = getVisitFromStorage();

    if (!visit.phoneNumber) {
      const visitAttributes = getVisitParameters();
      visit = await getVisitFromAPI(visitAttributes);
      storeVisit(visit);
    }

    if (visit.utm_location_name) {
        document.dispatchEvent(
          new CustomEvent('lawftyLocationReady', {
            detail: { name: visit.utm_location_name },
          }),
        );
    }

    return visit;
  };

  return {
    getVisitData,
  };
};
